/**
 * Brand Configuration
 *
 * Multi-brand configuration for storefront-multibrand platform.
 * Supports both Breitling and Gallet brands with brand-specific settings.
 *
 * Usage:
 * - Import `getBrandConfig()` to get current brand's config
 * - Import `getBrandByHostname()` for server-side brand detection
 * - Import specific brand configs for testing or explicit use
 */

export interface PreferenceCenterContract {
  page: 'GalletPreferenceCenter' | 'PreferenceCenter';
  idParam: 'uuid' | 'cid';
  identity: 'galletPreferenceUuid' | 'salesforceId';
  cacheBust: boolean;
}

/**
 * CRM configuration for user identification
 */
export interface CrmConfig {
  /** Placeholder string in URLs to replace with user ID */
  idPlaceholder: string;
  /** Metadata key to extract CRM ID from user object */
  userMetadataKey: string;
  /**
   * `null` renders My Preferences' unavailable state. UG's Visualforce page and
   * parameters are unconfirmed (Salesforce widget pending) — never fall back to
   * Breitling's `PreferenceCenter`.
   */
  preferenceCenter: PreferenceCenterContract | null;
}

/**
 * Brand-specific configuration
 */
export interface BrandConfig {
  /** Brand identifier */
  id: 'breitling' | 'gallet' | 'ug';
  /** Display name */
  name: string;
  /** Allowed domains for iframe embedding (security) */
  allowedIframeDomains: string[];
  /** CRM configuration (SalesForce for both brands) */
  crm: CrmConfig;
  /** Default contact phone number */
  defaultContactPhone: string;
  /** dataLayer event name pushed when OneTrust consent changes — must match the GTM trigger */
  consentChangedEvent: string;
}

/**
 * Breitling brand configuration
 */
export const BREITLING_CONFIG: BrandConfig = {
  id: 'breitling',
  name: 'Breitling',
  allowedIframeDomains: [
    'breitling.com',
    '*.breitling.com',
    'breitling.secure.force.com', // SalesForce forms
  ],
  crm: {
    idPlaceholder: '%%SFID%%',
    userMetadataKey: 'salesForceId',
    preferenceCenter: {
      page: 'PreferenceCenter',
      idParam: 'cid',
      identity: 'salesforceId',
      cacheBust: true,
    },
  },
  defaultContactPhone: '+41 32 654 54 54',
  consentChangedEvent: 'OneTrustBannerClosed',
};

/**
 * Gallet brand configuration
 */
export const GALLET_CONFIG: BrandConfig = {
  id: 'gallet',
  name: 'Gallet',
  allowedIframeDomains: [
    'gallet.com',
    '*.gallet.com',
    'galletwatches.com',
    '*.galletwatches.com',
    'gallet.secure.force.com', // SalesForce forms (if used)
  ],
  crm: {
    idPlaceholder: '%%SFID%%', // Same as Breitling - both use SalesForce
    userMetadataKey: 'salesForceId',
    preferenceCenter: {
      page: 'GalletPreferenceCenter',
      idParam: 'uuid',
      identity: 'galletPreferenceUuid',
      cacheBust: false,
    },
  },
  defaultContactPhone: '+1 800 XXX XXXX', // TODO: Update with actual Gallet number
  consentChangedEvent: 'onetrust_consent_changed',
};

/**
 * Universal Genève brand configuration
 */
export const UG_CONFIG: BrandConfig = {
  id: 'ug',
  name: 'Universal Genève',
  allowedIframeDomains: [
    'universalgeneve.com',
    '*.universalgeneve.com',
    'universalgeneve.secure.force.com', // SalesForce forms (if used)
  ],
  crm: {
    idPlaceholder: '%%SFID%%',
    userMetadataKey: 'salesForceId',
    preferenceCenter: null,
  },
  defaultContactPhone: '+41 22 319 00 00',
  consentChangedEvent: 'onetrust_consent_changed',
};

/**
 * All brand configurations
 */
export const BRAND_CONFIGS: Record<string, BrandConfig> = {
  breitling: BREITLING_CONFIG,
  gallet: GALLET_CONFIG,
  ug: UG_CONFIG,
};

/**
 * Trusted root domains for validating the x-breitling-domain header
 * set by the Cloudflare worker. Used in the proxy to prevent open-redirect
 * if the header is ever spoofed on a direct-to-origin request.
 */
const ALLOWED_PROXY_DOMAINS = [
  'breitling.com',
  'gallet.com',
  'galletwatches.com',
  'universalgeneve.com',
] as const;

export function isAllowedProxyDomain(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  return ALLOWED_PROXY_DOMAINS.some((domain) => lower === domain || lower.endsWith(`.${domain}`));
}

/**
 * Default brand (fallback)
 */
export const DEFAULT_BRAND_ID = 'gallet';

/**
 * Get brand config by ID
 */
export function getBrandConfigById(brandId: string): BrandConfig {
  return BRAND_CONFIGS[brandId] || BRAND_CONFIGS[DEFAULT_BRAND_ID];
}

/**
 * Get brand from hostname (server-side detection)
 *
 * @example
 * getBrandByHostname('www.breitling.com') // returns BREITLING_CONFIG
 * getBrandByHostname('shop.gallet.com') // returns GALLET_CONFIG
 */
export function getBrandByHostname(hostname: string): BrandConfig {
  const lowerHostname = hostname.toLowerCase();

  if (lowerHostname.includes('breitling')) {
    return BREITLING_CONFIG;
  }

  if (lowerHostname.includes('gallet')) {
    return GALLET_CONFIG;
  }

  if (lowerHostname.includes('universalgeneve')) {
    return UG_CONFIG;
  }

  // Fallback to default
  return BRAND_CONFIGS[DEFAULT_BRAND_ID];
}

/**
 * Get current brand config
 *
 * Client-side: Uses window.location.hostname
 * Server-side: Returns default brand (use getBrandByHostname with request headers)
 *
 * For server components, prefer passing brand explicitly or using getBrandByHostname
 * with the request's host header.
 */
export function getBrandConfig(): BrandConfig {
  if (typeof window !== 'undefined') {
    return getBrandByHostname(window.location.hostname);
  }

  // Server-side fallback - should be overridden with explicit brand
  // In practice, use getBrandByHostname(headers().get('host')) in server components
  return BRAND_CONFIGS[DEFAULT_BRAND_ID];
}

/**
 * Check if a URL is allowed for iframe embedding
 *
 * @param url - URL to validate
 * @param brandConfig - Brand config (defaults to current brand)
 * @returns true if URL is from an allowed domain
 */
export function isAllowedIframeDomain(url: string, brandConfig?: BrandConfig): boolean {
  const config = brandConfig || getBrandConfig();

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return config.allowedIframeDomains.some((pattern) => {
      // Handle wildcard patterns like *.breitling.com
      if (pattern.startsWith('*.')) {
        const baseDomain = pattern.slice(2);
        return hostname === baseDomain || hostname.endsWith('.' + baseDomain);
      }
      // Exact match
      return hostname === pattern;
    });
  } catch {
    // Invalid URL
    return false;
  }
}

/**
 * Additional allowed domains from environment (for flexibility)
 * Format: comma-separated list of domains
 */
export function getEnvAllowedDomains(): string[] {
  const envDomains = process.env.NEXT_PUBLIC_IFRAME_ALLOWED_DOMAINS;
  if (!envDomains) return [];
  return envDomains
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);
}

/**
 * Check if URL is allowed (combines brand config + env config)
 */
export function isUrlAllowedForIframe(url: string, brandConfig?: BrandConfig): boolean {
  // Check brand-specific domains
  if (isAllowedIframeDomain(url, brandConfig)) {
    return true;
  }

  // Check environment-configured domains
  const envDomains = getEnvAllowedDomains();
  if (envDomains.length > 0) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      return envDomains.some((domain) => {
        if (domain.startsWith('*.')) {
          const baseDomain = domain.slice(2);
          return hostname === baseDomain || hostname.endsWith('.' + baseDomain);
        }
        return hostname === domain;
      });
    } catch {
      return false;
    }
  }

  return false;
}
